import React, { useState, useEffect } from 'react'
import { usePayment } from '../hooks/usePayment'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../hooks/useLanguage'
import { tripsApi } from '../services/api'
import toast from 'react-hot-toast'

interface Payment {
  id: string
  amount: number
  tranche: number
  status: 'pending' | 'paid' | 'failed' | 'refunded'
  paidAt?: string | null
  checkoutSessionId?: string
}

interface PaymentButtonsProps {
  bookingId: string
  tripId: string
  tripName: string
  totalAmount: number
  firstTrancheAmount: number
  secondTrancheAmount: number
  payments: Payment[]
  onPaymentSuccess?: () => void
  className?: string
}

export const PaymentButtons: React.FC<PaymentButtonsProps> = ({
  bookingId,
  tripId,
  tripName,
  totalAmount,
  firstTrancheAmount,
  secondTrancheAmount,
  payments,
  onPaymentSuccess,
  className = ''
}) => {
  const { t } = useLanguage()
  const { handlePayment, isProcessing } = usePayment()
  const { user } = useAuth()
  const [tripAvailability, setTripAvailability] = useState<{
    isFull: boolean
    availableSpots: number | null
    maxParticipants: number | null
  } | null>(null)
  const [checkingAvailability, setCheckingAvailability] = useState(false)
  
  // Extract payment information from the payments array
  const firstPayment = payments.find(p => p.tranche === 1)
  const secondPayment = payments.find(p => p.tranche === 2)
  
  const paymentInfo = {
    firstPayment: firstPayment ? {
      id: firstPayment.id,
      amount: firstPayment.amount,
      status: firstPayment.status,
      paidAt: firstPayment.paidAt
    } : null,
    secondPayment: secondPayment ? {
      id: secondPayment.id,
      amount: secondPayment.amount,
      status: secondPayment.status,
      paidAt: secondPayment.paidAt
    } : null,
    canPayFirst: !firstPayment || firstPayment.status === 'failed',
    canPaySecond: firstPayment && firstPayment.status === 'paid' && (!secondPayment || secondPayment.status === 'failed')
  }

  // Check trip availability when component mounts or when trying to pay deposit
  const checkTripAvailability = async () => {
    try {
      setCheckingAvailability(true)
      const response = await tripsApi.checkAvailability(tripId)
      if (response.success) {
        setTripAvailability(response.availability)
      }
    } catch (error) {
      console.error('Error checking trip availability:', error)
      toast.error('Errore nel verificare la disponibilità del viaggio')
    } finally {
      setCheckingAvailability(false)
    }
  }

  useEffect(() => {
    // Only check availability if we need to pay the first payment (deposit)
    if (paymentInfo.canPayFirst) {
      checkTripAvailability()
    }
  }, [tripId, paymentInfo.canPayFirst])

  const handlePaymentClick = async (trancheType: 'first' | 'second') => {
    // For first payment (deposit), check if trip is full
    if (trancheType === 'first') {
      if (tripAvailability?.isFull) {
        toast.error(t('payment.tripFullMessage'))
        return
      }
      
      // If we don't have availability info yet, check it first
      if (!tripAvailability && !checkingAvailability) {
        await checkTripAvailability()
        // Re-check after getting availability info
        const updatedAvailability = await tripsApi.checkAvailability(tripId)
        if (updatedAvailability.success && updatedAvailability.availability.isFull) {
          toast.error(t('payment.tripFullMessage'))
          return
        }
      }
    }
    
    // Retrieve discount code from localStorage if available
    let codiceScontoId: string | undefined;
    if (tripId && user?.id) {
      const savedCodeId = localStorage.getItem(`codiceSconto_${tripId}_${user.id}`);
      if (savedCodeId) {
        codiceScontoId = savedCodeId;
      }
    }
    
    const result = await handlePayment(trancheType, bookingId, tripName, codiceScontoId)
    
    if (result.success && onPaymentSuccess) {
      onPaymentSuccess()
    }
  }

  const handleResumePayment = async (paymentId: string) => {
    try {
      // Check if trip is full before resuming payment
      if (tripAvailability?.isFull) {
        toast.error(t('payment.tripFullMessage'))
        return
      }
      
      // If we don't have availability info yet, check it first
      if (!tripAvailability && !checkingAvailability) {
        await checkTripAvailability()
        // Re-check after getting availability info
        const updatedAvailability = await tripsApi.checkAvailability(tripId)
        if (updatedAvailability.success && updatedAvailability.availability.isFull) {
          toast.error(t('payment.tripFullMessage'))
          return
        }
      }
      
      const result = await tripsApi.resumePayment(paymentId)
      if (result.success && result.checkoutUrl) {
        window.location.href = result.checkoutUrl
      }
    } catch (error) {
      console.error('Error resuming payment:', error)
      toast.error('Errore nel riprendere il pagamento. Riprova.')
    }
  }


  return (
    <div className={`space-y-3 ${className}`}>
      {/* Show trip full message if trying to pay deposit and trip is full */}
      {paymentInfo?.canPayFirst && tripAvailability?.isFull && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Viaggio Completo
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  Il viaggio ha raggiunto il numero massimo di partecipanti ({tripAvailability.maxParticipants}). 
                  Non è possibile effettuare il pagamento dell'acconto.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {(paymentInfo?.firstPayment?.status === 'pending' || paymentInfo?.firstPayment?.status === 'failed') ? (
        tripAvailability?.isFull ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm text-center font-semibold">
            {t('payment.cannotPayTripFull')}
          </div>
        ) : (
          <button
            onClick={() => paymentInfo.firstPayment && handleResumePayment(paymentInfo.firstPayment.id)}
            disabled={checkingAvailability}
            className={`w-full font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center ${
              checkingAvailability
                ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                : paymentInfo.firstPayment?.status === 'failed' 
                ? 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white' 
                : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white'
            }`}
          >
            {checkingAvailability ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Verificando disponibilità...
              </>
            ) : paymentInfo.firstPayment?.status === 'failed' ? (
              t('payment.retryPayment') 
            ) : (
              t('payment.resumeDepositPayment')
            )}
          </button>
        )
      ) : (
        <button
          onClick={() => handlePaymentClick('first')}
          disabled={isProcessing !== null || !paymentInfo?.canPayFirst || tripAvailability?.isFull || checkingAvailability}
          className={`w-full font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center ${
            paymentInfo?.firstPayment?.status === 'paid'
              ? 'bg-green-100 text-green-800 cursor-not-allowed'
              : tripAvailability?.isFull || checkingAvailability
              ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
              : 'bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed'
          }`}
        >
          {isProcessing === 'first' ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('payment.processing')}
            </>
          ) : checkingAvailability ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Verificando disponibilità...
            </>
          ) : tripAvailability?.isFull ? (
            t('payment.tripFull')
          ) : paymentInfo?.firstPayment?.status === 'paid' ? (
            t('payment.depositPaid')
          ) : (
            t('payment.payDeposit').replace('{amount}', firstTrancheAmount.toString())
          )}
        </button>
      )}

      {(paymentInfo?.secondPayment?.status === 'pending' || paymentInfo?.secondPayment?.status === 'failed') ? (
        <button
          onClick={() => paymentInfo.secondPayment && handleResumePayment(paymentInfo.secondPayment.id)}
          className={`w-full font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center ${
            paymentInfo.secondPayment?.status === 'failed' 
              ? 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white' 
              : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white'
          }`}
        >
          {paymentInfo.secondPayment?.status === 'failed' 
            ? t('payment.retryPayment') 
            : t('payment.resumeBalancePayment')}
        </button>
      ) : (
        <button
          onClick={() => handlePaymentClick('second')}
          disabled={isProcessing !== null || !paymentInfo?.canPaySecond}
          className={`w-full font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center ${
            paymentInfo?.secondPayment?.status === 'paid'
              ? 'bg-green-100 text-green-800 cursor-not-allowed'
              : !paymentInfo?.canPaySecond
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed'
          }`}
        >
          {isProcessing === 'second' ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('payment.processing')}
            </>
          ) : paymentInfo?.secondPayment?.status === 'paid' ? (
            t('payment.balancePaid')
          ) : !paymentInfo?.canPaySecond ? (
            t('payment.payDepositFirst')
          ) : (
            t('payment.payBalance').replace('{amount}', secondTrancheAmount.toString())
          )}
        </button>
      )}

      <div className="text-xs text-gray-600 text-center">
        <p>{t('payment.securityInfo.stripeSecure')}</p>
        <p>{t('payment.securityInfo.noCardStorage')}</p>
        <p>{t('payment.securityInfo.balanceDue')}</p>
      </div>
    </div>
  )
} 